import React, { useState, useEffect, useMemo } from 'react';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Repeat1,
  Volume2,
  VolumeX,
  Volume1,
  ListMusic,
  Music,
  Plus,
  CheckCircle2,
  ScrollText,
} from 'lucide-react';
import SongListPanel from './SongListPanel';
import PlaylistPanel from './PlaylistPanel';
import LyricsModal from '../LyricsModal';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const formatTime = (sec) => {
  if (!sec || isNaN(sec) || !isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

function MusicPlayer() {
  const {
    playlist,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    shuffleMode,
    repeatMode,
    showSongList,
    showPlaylist,
    togglePlay,
    playNext,
    playPrev,
    seek,
    setVolume,
    toggleMute,
    toggleShuffle,
    cycleRepeat,
    toggleSongList,
    togglePlaylist,
    isDraggingRef,
    toast,
    setLyricsBarVisible,
  } = useMusicPlayer();

  const current = currentIndex !== null ? playlist[currentIndex] : null;
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const [lyricsModal, setLyricsModal] = useState(null);

  // 싱크 가사 파싱
  const parsedSyncLines = useMemo(() => {
    if (!current?.syncLyrics) return [];
    return current.syncLyrics.split('\n').filter(l => l.trim()).map(line => {
      const match = line.match(/^\[(\d{2}):(\d{2}(?:\.\d+)?)\](.*)$/);
      if (!match) return null;
      return { time: parseInt(match[1]) * 60 + parseFloat(match[2]), text: match[3] };
    }).filter(Boolean);
  }, [current?.syncLyrics]);

  // 현재 가사 줄 (첫 줄 시간 전에는 첫 줄 표시)
  const currentLyricLine = useMemo(() => {
    if (parsedSyncLines.length === 0) return '';
    if (currentTime < parsedSyncLines[0].time) return parsedSyncLines[0].text;
    let line = '';
    for (let i = 0; i < parsedSyncLines.length; i++) {
      if (parsedSyncLines[i].time <= currentTime) line = parsedSyncLines[i].text;
      else break;
    }
    return line;
  }, [parsedSyncLines, currentTime]);

  // 가사바 표시 여부를 context에 반영
  useEffect(() => {
    const visible = !!(current && (current.syncLyrics || current.lyrics));
    setLyricsBarVisible(visible);
  }, [current, setLyricsBarVisible]);

  const handleProgressChange = (e) => {
    const val = Number(e.target.value);
    seek((val / 100) * duration);
  };

  const handleProgressMouseDown = () => {
    isDraggingRef.current = true;
  };

  const handleProgressMouseUp = (e) => {
    isDraggingRef.current = false;
    const val = Number(e.target.value);
    seek((val / 100) * duration);
  };

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;

  return (
    <>
      {/* 재생목록 추가 토스트 */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60] flex items-center gap-2 px-4 py-3 bg-[#1e2e46] border border-white/10 rounded-xl text-white text-sm shadow-xl pointer-events-none">
          <CheckCircle2 size={15} className="text-sky-400 shrink-0" />
          {toast}
        </div>
      )}

      {/* 패널 바깥 클릭 시 닫기용 백드롭 */}
      {(showSongList || showPlaylist) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            if (showSongList) toggleSongList();
            if (showPlaylist) togglePlaylist();
          }}
        />
      )}
      {showSongList && <SongListPanel />}
      {showPlaylist && <PlaylistPanel />}

      {/* 가사 라인 바 (싱크가사가 있는 곡만) */}
      {current && current.syncLyrics && currentLyricLine && (
        <div
          onClick={() => setLyricsModal({
            lyrics: current.lyrics,
            syncLyrics: current.syncLyrics,
            songName: current.nameKr,
            artistName: current.artistKr || '',
            songId: current.id,
          })}
          className="fixed bottom-[92px] sm:bottom-[68px] left-0 right-0 z-[45] bg-[#182334]/95 backdrop-blur-sm border-t border-white/5 cursor-pointer hover:bg-[#1e2e46]/95 transition-colors">
          <div className="max-w-2xl mx-auto px-4 py-1.5 flex items-center justify-center">
            <p className="text-sm text-sky-200 truncate text-center">{currentLyricLine}</p>
          </div>
        </div>
      )}

      {/* 가사 버튼 (싱크 없이 가사만 있는 곡) */}
      {current && current.lyrics && !current.syncLyrics && (
        <div className="fixed bottom-[92px] sm:bottom-[68px] left-0 right-0 z-[45] bg-[#182334]/95 backdrop-blur-sm border-t border-white/5">
          <div className="max-w-2xl mx-auto px-4 py-1.5 flex items-center justify-center">
            <button
              onClick={() => setLyricsModal({
                lyrics: current.lyrics,
                syncLyrics: current.syncLyrics,
                songName: current.nameKr,
                artistName: current.artistKr || '',
                songId: current.id,
              })}
              className="flex items-center gap-1.5 text-slate-400 hover:text-sky-400 text-sm transition-colors">
              <ScrollText size={13} />
              가사 보기
            </button>
          </div>
        </div>
      )}

      {/* 하단 고정 플레이어 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#182334] border-t border-white/15 select-none overflow-hidden">
        {/* ═══ 모바일 레이아웃 ═══ */}
        <div className="sm:hidden h-[92px]">
          {playlist.length === 0 ? (
            <div className="flex items-center justify-center gap-3 py-4">
              <Music size={16} className="text-slate-600" />
              <span className="text-slate-500 text-sm">플레이리스트가 비어 있습니다</span>
              <button onClick={toggleSongList} className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 rounded-lg text-white text-xs font-medium transition-colors">
                <Plus size={12} />곡 찾기
              </button>
            </div>
          ) : (
            <>
              {/* 곡 정보 */}
              {current && (
                <div className="text-center px-4 pt-1.5 pb-0">
                  <p className="text-xs text-white font-medium truncate leading-snug">
                    {current.nameKr}
                    {current.artistKr && <span className="text-slate-400"> · {current.artistKr}</span>}
                  </p>
                </div>
              )}
              {/* 컨트롤 행: 곡목록 | 이전 | 재생 | 다음 | 재생목록 */}
              <div className="flex items-center justify-center gap-3 px-3 pt-1.5 pb-0.5">
                <button onClick={toggleSongList} title="곡 목록"
                  className={`p-2 rounded shrink-0 transition-colors ${showSongList ? 'text-sky-400 bg-sky-500/10' : 'text-slate-500 hover:text-white'}`}>
                  <Music size={18} />
                </button>
                <button onClick={playPrev} title="이전 곡" className="p-1 text-slate-300 hover:text-white transition-colors">
                  <SkipBack size={18} fill="currentColor" />
                </button>
                <button onClick={togglePlay} title={isPlaying ? '일시정지' : '재생'}
                  className={`play-btn w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 shadow${isPlaying ? ' playing' : ''}`}>
                  {isPlaying ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" className="ml-0.5" />}
                </button>
                <button onClick={playNext} title="다음 곡" className="p-1 text-slate-300 hover:text-white transition-colors">
                  <SkipForward size={18} fill="currentColor" />
                </button>
                <button onClick={togglePlaylist} title="플레이리스트"
                  className={`p-2 rounded shrink-0 transition-colors ${showPlaylist ? 'text-sky-400 bg-sky-500/10' : 'text-slate-500 hover:text-white'}`}>
                  <ListMusic size={18} />
                </button>
              </div>
              {/* 진행바 (풀 너비) */}
              <div className="flex items-center gap-1.5 px-3 pb-2.5">
                <span className="text-slate-500 text-[10px] w-7 text-right shrink-0 tabular-nums">{formatTime(currentTime)}</span>
                <div className="relative flex-1 flex items-center">
                  <input type="range" min={0} max={100} step={0.1} value={progressPct}
                    onChange={handleProgressChange} onMouseDown={handleProgressMouseDown} onMouseUp={handleProgressMouseUp}
                    onTouchStart={handleProgressMouseDown} onTouchEnd={handleProgressMouseUp}
                    className="player-progress w-full" style={{ '--progress': `${progressPct}%` }} />
                </div>
                <span className="text-slate-500 text-[10px] w-7 shrink-0 tabular-nums">{formatTime(duration - currentTime)}</span>
              </div>
            </>
          )}
        </div>

        {/* ═══ 데스크탑 레이아웃 ═══ */}
        <div className="hidden sm:flex h-[68px] items-center px-4 gap-3">
          {playlist.length === 0 ? (
            <div className="flex-1 flex items-center justify-center gap-3">
              <Music size={16} className="text-slate-600" />
              <span className="text-slate-500 text-sm">플레이리스트가 비어 있습니다</span>
              <button onClick={toggleSongList} className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 rounded-lg text-white text-xs font-medium transition-colors">
                <Plus size={12} />곡 찾기
              </button>
            </div>
          ) : (
            <>
              {/* 좌측: 앨범아트 + 곡 정보 */}
              <div className="flex items-center gap-3 w-[160px] md:w-[220px] lg:w-[280px] shrink-0">
                <img
                  src={current?.albumImage || FALLBACK_IMG}
                  alt="album"
                  className={`w-11 h-11 rounded-full object-cover shrink-0 shadow vinyl-spin${isPlaying ? '' : ' paused'}`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-white text-sm font-medium truncate leading-tight">{current?.nameKr || '—'}</p>
                    {isPlaying && (
                      <span className="eq-bars text-sky-400 shrink-0">
                        <span /><span /><span /><span />
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-xs truncate mt-0.5">{current?.artistKr || '—'}</p>
                </div>
              </div>

              {/* 중앙: 컨트롤 + 진행바 */}
              <div className="flex-1 flex flex-col items-center justify-center gap-1.5 min-w-0 overflow-hidden">
                <div className="flex items-center gap-3">
                  <button onClick={toggleShuffle} title="셔플"
                    className={`p-1 rounded transition-colors ${shuffleMode ? 'text-sky-400' : 'text-slate-500 hover:text-slate-200'}`}>
                    <Shuffle size={14} />
                  </button>
                  <button onClick={playPrev} title="이전 곡" className="p-1 text-slate-300 hover:text-white transition-colors">
                    <SkipBack size={18} fill="currentColor" />
                  </button>
                  <button onClick={togglePlay} title={isPlaying ? '일시정지' : '재생'}
                    className={`play-btn w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 shadow${isPlaying ? ' playing' : ''}`}>
                    {isPlaying ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" className="ml-0.5" />}
                  </button>
                  <button onClick={playNext} title="다음 곡" className="p-1 text-slate-300 hover:text-white transition-colors">
                    <SkipForward size={18} fill="currentColor" />
                  </button>
                  <button onClick={cycleRepeat} title={repeatMode === 'none' ? '반복 없음' : repeatMode === 'all' ? '전체 반복' : '한 곡 반복'}
                    className={`p-1 rounded transition-colors ${repeatMode !== 'none' ? 'text-sky-400' : 'text-slate-500 hover:text-slate-200'}`}>
                    <RepeatIcon size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2 w-full max-w-md">
                  <span className="text-slate-500 text-xs w-8 text-right shrink-0 tabular-nums">{formatTime(currentTime)}</span>
                  <div className="relative flex-1 flex items-center">
                    <input type="range" min={0} max={100} step={0.1} value={progressPct}
                      onChange={handleProgressChange} onMouseDown={handleProgressMouseDown} onMouseUp={handleProgressMouseUp}
                      onTouchStart={handleProgressMouseDown} onTouchEnd={handleProgressMouseUp}
                      className="player-progress w-full" style={{ '--progress': `${progressPct}%` }} />
                  </div>
                  <span className="text-slate-500 text-xs w-8 shrink-0 tabular-nums">{formatTime(duration - currentTime)}</span>
                </div>
              </div>

              {/* 우측: 볼륨 + 패널 버튼 */}
              <div className="flex items-center gap-1.5 shrink-0 justify-end md:w-[220px] lg:w-[280px]">
                <div className="hidden md:flex items-center gap-1.5">
                  <button onClick={toggleMute} className="p-1 text-slate-400 hover:text-white transition-colors">
                    <VolumeIcon size={15} />
                  </button>
                  <input type="range" min={0} max={1} step={0.01} value={isMuted ? 0 : volume}
                    onChange={(e) => setVolume(Number(e.target.value))} className="player-volume w-[72px]" />
                </div>
                <div className="w-px h-4 bg-white/10 hidden md:block" />
                <button onClick={togglePlaylist} title="플레이리스트"
                  className={`p-1.5 rounded transition-colors ${showPlaylist ? 'text-sky-400 bg-sky-500/10' : 'text-slate-400 hover:text-white'}`}>
                  <ListMusic size={16} />
                </button>
                <button onClick={toggleSongList} title="곡 목록"
                  className={`p-1.5 rounded transition-colors ${showSongList ? 'text-sky-400 bg-sky-500/10' : 'text-slate-400 hover:text-white'}`}>
                  <Music size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 가사 모달 */}
      {lyricsModal && (
        <LyricsModal
          {...lyricsModal}
          currentTime={currentTime}
          onClose={() => setLyricsModal(null)}
        />
      )}
    </>
  );
}

export default MusicPlayer;
